import React, { useState, useEffect } from 'react';
import './ColorVariantSlideshow.css';
import type { ColorOption } from '../types/productTypes';
import { debug } from '../utils/debug';

// Function to get default colors for common color names when hexCode isn't available
const getDefaultColorForName = (colorName: string): string => {
  const colorMap: Record<string, string> = {
    'black': '#333',
    'white': '#f0f0f0',
    'blue': '#a4c6e4',
    'red': '#e57373',
    'pink': '#f8c3cd',
    'green': '#aed581',
    'yellow': '#fff59d',
    'purple': '#ce93d8',
    'gray': '#bdbdbd',
    'gold': '#f1c40f',
    'silver': '#e0e0e0',
    'space-gray': '#757575',
    'midnight': '#263238',
    'starlight': '#fafafa',
    'product-red': '#e53935',
    'sierra-blue': '#90caf9',
    'alpine-green': '#558b2f',
    'deep-purple': '#6a1b9a',
    'graphite': '#616161',
    'pacific-blue': '#0288d1',
  };
  
  return colorMap[colorName] || '#bdbdbd'; // Default to gray if no match
};

interface ColorVariantSlideshowProps {
  defaultImage: string; // Default product image
  productName: string;
  colorOptions: ColorOption[];
  onColorChange?: (colorName: string) => void;
}

const ColorVariantSlideshow: React.FC<ColorVariantSlideshowProps> = ({
  defaultImage,
  productName,
  colorOptions,
  onColorChange
}) => {
  // Create a map of color variants with images
  const [variants, setVariants] = useState<ColorOption[]>([]);
  const [activeColor, setActiveColor] = useState<string>('');
  const [activeImage, setActiveImage] = useState<string>(defaultImage);

  // Initialize variants with specific color images or default image
  useEffect(() => {
    debug(`ColorVariantSlideshow - ${productName} - Color options:`, colorOptions);
    
    // Log hexCodes for debugging
    if (colorOptions && colorOptions.length > 0) {
      colorOptions.forEach(color => {
        debug(`Color ${color.name} has hexCode: ${color.hexCode || 'not set'}, using style:`, 
          color.hexCode 
            ? { backgroundColor: color.hexCode } 
            : { backgroundColor: getDefaultColorForName(color.color) }
        );
      });
    }

    if (!colorOptions || colorOptions.length === 0) {
      debug(`No color options provided for ${productName}, using default`);
      // Handle case when colorOptions is empty or undefined
      setVariants([{ name: 'Default', color: 'gray', hexCode: '#bdbdbd', isActive: true, image: defaultImage }]);
      setActiveColor('Default');
      setActiveImage(defaultImage);
      return;
    }

    // Initialize the variants, using color-specific images if available, defaultImage otherwise
    const initialVariants = colorOptions.map(color => ({
      ...color,
      image: color.image || defaultImage
    }));

    debug(`${productName} - Initialized variants:`, initialVariants);

    // Find the initially active color - safely access with optional chaining
    const activeOption = colorOptions.find(color => color.isActive);
    debug(`${productName} - Active option found:`, activeOption);

    const initialActive = activeOption && activeOption.name
      ? activeOption.name
      : (colorOptions[0] ? colorOptions[0].name : 'None');

    debug(`${productName} - Initial active color:`, initialActive);

    // Set the active image to the selected color's image
    const selectedVariant = initialVariants.find(v => v.name === initialActive);
    debug(`${productName} - Selected variant:`, selectedVariant);

    const initialImage = selectedVariant && selectedVariant.image
      ? selectedVariant.image
      : defaultImage;

    debug(`${productName} - Initial image:`, initialImage);

    setVariants(initialVariants);
    setActiveColor(initialActive);
    setActiveImage(initialImage);
  }, [colorOptions, defaultImage]);

  // Handle color swatch click
  const handleColorSwatchClick = (colorName: string) => {
    if (colorName === activeColor) {
      return; // No change if selecting the same color
    }

    setActiveColor(colorName);

    // Update active status in variants
    const updatedVariants = variants.map(variant => ({
      ...variant,
      isActive: variant.name === colorName
    }));

    setVariants(updatedVariants);

    // Set the active image to the selected color's image immediately
    const selectedVariant = updatedVariants.find(v => v.name === colorName);
    if (selectedVariant && selectedVariant.image) {
      setActiveImage(selectedVariant.image);
    }

    // Call the onColorChange prop if provided
    if (onColorChange) {
      onColorChange(colorName);
    }
  };

  return (
    <div className="color-variant-slideshow">
      <img
        src={activeImage}
        alt={`${productName} in ${activeColor}`}
        className="product-image"
      />

      <div className="color-swatches">
        {variants.map((variant, index) => (
          <span
            key={index}
            className={`color-swatch ${variant.name === activeColor ? 'active' : ''}`}
            title={variant.name}
            onClick={() => handleColorSwatchClick(variant.name)}
            style={{
              backgroundColor: variant.hexCode || 
                (variant.color === 'default' ? '#bdbdbd' : getDefaultColorForName(variant.color))
            }}
          ></span>
        ))}
      </div>

      <p className="color-name">{activeColor}</p>
    </div>
  );
};

export default ColorVariantSlideshow;
